import React, { useState, useMemo } from 'react';
import { Users, Mail, Phone, Calendar, User, RefreshCw, Download, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const OTASignupsTab = ({ 
  signups, 
  loading, 
  sortField, 
  sortOrder, 
  onSort, 
  onRefresh, 
  onExport, 
  formatDate 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [ageFilter, setAgeFilter] = useState('all');

  const filteredSignups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return signups.filter(s => {
      if (q) {
        const hay = `${s.player_name || ''} ${s.parent_name || ''} ${s.email || ''} ${s.phone || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (ageFilter !== 'all') {
        const a = Number(s.age);
        const [lo, hi] = ageFilter.split('-').map(Number);
        if (Number.isNaN(a) || a < lo || a > hi) return false;
      }
      return true;
    });
  }, [signups, searchQuery, ageFilter]);

  const sortedSignups = [...filteredSignups].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const getAgeGroupStats = () => {
    const groups = { '5-6': 0, '7-8': 0, '9-10': 0, '11-12': 0, '13-14': 0, '15+': 0 };
    
    signups.forEach(s => {
      const age = s.age;
      if (age <= 6) groups['5-6']++;
      else if (age <= 8) groups['7-8']++;
      else if (age <= 10) groups['9-10']++;
      else if (age <= 12) groups['11-12']++;
      else if (age <= 14) groups['13-14']++;
      else groups['15+']++;
    });
    
    return groups;
  };

  const ageStats = getAgeGroupStats();

  return (
    <>
      {/* Stats Cards */}
      <div className="stats-grid">
        <Card className="stat-card total-card">
          <CardContent>
            <div className="stat-content">
              <Users className="stat-icon" size={32} />
              <div>
                <div className="stat-value">{signups.length}</div>
                <div className="stat-label">Total Signups</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {Object.entries(ageStats).map(([group, count]) => (
          count > 0 && (
            <Card key={group} className="stat-card age-card">
              <CardContent>
                <div className="stat-content">
                  <div className="age-badge">{group}</div>
                  <div>
                    <div className="stat-value">{count}</div>
                    <div className="stat-label">Players</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        ))}
      </div>

      {/* Actions Bar */}
      <div className="actions-bar">
        <Button onClick={onRefresh} className="action-btn refresh-btn" disabled={loading}>
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
        <Button onClick={onExport} className="action-btn export-btn" disabled={signups.length === 0}>
          <Download size={18} />
          Export CSV
        </Button>
      </div>

      {/* Search + Filter */}
      <div
        className="admin-search-bar"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          alignItems: 'center',
          margin: '0 0 1rem',
          padding: '0.75rem',
          background: 'rgba(255, 215, 0, 0.04)',
          border: '1px solid rgba(255, 215, 0, 0.18)',
          borderRadius: '10px',
        }}
      >
        <div style={{ position: 'relative', flex: '1 1 280px', minWidth: '220px' }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#FFD700' }} />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search player name, parent, email, or phone…"
            data-testid="ota-search-input"
            style={{
              width: '100%',
              padding: '0.5rem 0.5rem 0.5rem 2rem',
              borderRadius: '8px',
              border: '1px solid rgba(255,215,0,0.3)',
              background: '#0a0a0a',
              color: '#fff',
              fontSize: '0.95rem',
            }}
          />
        </div>
        <select
          value={ageFilter}
          onChange={(e) => setAgeFilter(e.target.value)}
          data-testid="ota-age-filter"
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '8px',
            border: '1px solid rgba(255,215,0,0.3)',
            background: '#0a0a0a',
            color: '#fff',
            fontSize: '0.9rem',
          }}
        >
          <option value="all">All Ages</option>
          <option value="5-6">5–6</option>
          <option value="7-8">7–8</option>
          <option value="9-10">9–10</option>
          <option value="11-12">11–12</option>
          <option value="13-14">13–14</option>
          <option value="15-99">15+</option>
        </select>
        <span style={{ color: '#FFD700', fontSize: '0.85rem', fontWeight: 600 }} data-testid="ota-filtered-count">
          {sortedSignups.length} of {signups.length}
        </span>
      </div>

      {/* Signups Table */}
      <Card className="table-card">
        <CardHeader>
          <CardTitle>OTA Training Registrations</CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            if (loading) {
              return (
                <div className="loading-state">
                  <RefreshCw className="animate-spin" size={32} />
                  <p>Loading signups...</p>
                </div>
              );
            }
            if (signups.length === 0) {
              return (
                <div className="empty-state">
                  <Users size={48} />
                  <h3>No Signups Yet</h3>
                  <p>OTA training signups will appear here once players register.</p>
                </div>
              );
            }
            return (
            <div className="table-wrapper">
              <table className="signups-table" data-testid="signups-table">
                <thead>
                  <tr>
                    <th onClick={() => onSort('player_name')} className="sortable">
                      Player Name
                      {sortField === 'player_name' && <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                    <th onClick={() => onSort('age')} className="sortable">
                      Age
                      {sortField === 'age' && <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                    <th>Parent/Guardian</th>
                    <th>Contact Info</th>
                    <th onClick={() => onSort('created_at')} className="sortable">
                      Signup Date
                      {sortField === 'created_at' && <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSignups.map((signup) => (
                    <tr key={signup.id}>
                      <td>
                        <div className="player-info">
                          <User size={16} className="info-icon" />
                          <span className="player-name">{signup.player_name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="age-pill">{signup.age} yrs</span>
                      </td>
                      <td>
                        <div className="parent-info">
                          <User size={14} className="info-icon-muted" />
                          <span className="parent-name">{signup.parent_name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="signups-contact-info">
                          <div className="contact-item">
                            <Mail size={14} />
                            <a href={`mailto:${signup.email}`}>{signup.email}</a>
                          </div>
                          <div className="contact-item">
                            <Phone size={14} />
                            <a href={`tel:${signup.phone}`}>{signup.phone}</a>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="date-info">
                          <Calendar size={14} />
                          {formatDate(signup.created_at)}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${signup.status}`}>
                          {signup.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}
        </CardContent>
      </Card>
    </>
  );
};

export default OTASignupsTab;
